<template>
  <div class="analysis-container">
    <!-- 顶部 -->
    <div class="top-bar">
      <button class="ghost-btn" @click="goBack">
        <span class="icon">←</span> 返回
      </button>
      <div class="title">
        {{ toolInfo.title || '组间比较（小提琴/箱线图）统计检验' }}
      </div>
      <div class="actions">
        <button class="tool-btn" @click="showGuide">❓ 使用指南</button>
      </div>
    </div>

    <!-- 主体区域 -->
    <div class="main-layout">
      <!-- 左侧结果展示 -->
      <div class="left-panel">
        <!-- 顶部按钮栏 -->
        <div class="chart-toolbar">
          <button class="tool-btn" :class="{ primary: activeTab === 'guide' }" @click="activeTab = 'guide'">❓ 使用指南</button>
          <button class="tool-btn" :class="{ primary: activeTab === 'logs' }" @click="activeTab = 'logs'" :disabled="!canPreviewLog">📋 预览日志</button>
          <button class="tool-btn" :class="{ primary: activeTab === 'result' }" @click="activeTab = 'result'" :disabled="!taskInfo || taskInfo.status !== 'SUCCESS'">📁 结果</button>
          <button class="tool-btn" :class="{ primary: activeTab === 'comments' }" @click="activeTab = 'comments'">
            💬 评论({{ commentCount }})
          </button>
          <button class="tool-btn" :class="{ primary: activeTab === 'preview' }" @click="activeTab = 'preview'; loadPdfPreview()" :disabled="!taskInfo || taskInfo.status !== 'SUCCESS'">
            👁 结果预览
          </button>
        </div>

        <!-- 内容区域 -->
        <div class="content-area">
          <!-- 图表/结果展示 -->
          <div v-if="activeTab === 'result'" class="result-panel">
            <div v-if="taskInfo && taskInfo.status === 'SUCCESS'" class="pdf-preview-container">
              <div class="success-message">
                <span class="success-icon">✅</span>
                <span>任务执行成功！结果已生成</span>
              </div>
              <div v-if="taskInfo.outputDir" class="output-dir-info">
                <span class="label">输出目录：</span>
                <code>{{ taskInfo.outputDir }}</code>
              </div>
              <div class="result-actions">
                <button class="primary-btn" @click="downloadResult">⬇ 下载文件(.zip)</button>
              </div>
            </div>
            <div v-else-if="taskInfo && taskInfo.status === 'RUNNING'" class="running-panel">
              <div class="running-animation">⏳ 任务执行中...</div>
              <div class="progress-info">当前进度: {{ taskInfo.progress }}%</div>
            </div>
            <div v-else class="empty-result">
              <ViolinBoxPlot v-if="!taskInfo" />
              <div v-else class="no-result">任务 {{ taskInfo.status === 'FAILED' ? '执行失败' : '等待中' }}</div>
            </div>
          </div>

          <!-- 日志展示 -->
          <div v-else-if="activeTab === 'logs'" class="logs-panel">
            <div v-if="logs.length > 0" class="logs-container">
              <div v-for="(log, index) in logs" :key="index" :class="['log-item', log.type]">
                <span class="log-time">{{ formatTime(log.time) }}</span>
                <span class="log-type">[{{ log.type }}]</span>
                <span class="log-message">{{ log.message }}</span>
              </div>
            </div>
            <div v-else class="empty-logs">暂无日志</div>
          </div>

          <!-- 评论面板 -->
          <div v-else-if="activeTab === 'comments'" class="comments-panel">
            <!-- 评分概览 -->
            <div class="comments-header">
              <div class="rating-score-box">
                <div class="rating-big-score">{{ avgRating }}</div>
                <div class="rating-big-stars">
                  <span v-for="n in 5" :key="n" class="big-star" :class="{ filled: n <= Math.round(avgRating) }">★</span>
                </div>
                <div class="rating-total">{{ commentCount }} 条评论</div>
              </div>
            </div>
            
            <!-- 发表评论 -->
            <div class="comment-form">
              <div class="form-title">📝 发表评论</div>
              <div class="rating-selector">
                <span class="rating-tip">点击星星评分：</span>
                <div class="star-input-group">
                  <span 
                    v-for="n in 5" 
                    :key="n" 
                    class="star-input" 
                    :class="{ filled: n <= (hoverRating || newCommentRating) }"
                    @click="setRating(n)"
                    @mouseenter="hoverRating = n"
                    @mouseleave="hoverRating = 0"
                  >★</span>
                </div>
                <span class="rating-text">{{ ['','非常差','差','一般','好','非常好'][hoverRating || newCommentRating] }}</span>
              </div>
              <textarea 
                v-model="newCommentContent" 
                class="comment-textarea"
                placeholder="分享您的使用体验..."
                rows="3"
              ></textarea>
              <div class="form-actions">
                <button class="primary-btn submit-comment-btn" @click="submitComment">✉️ 发表评论</button>
              </div>
            </div>
            
            <!-- 评论列表 -->
            <div class="comments-list">
              <div class="list-title">💬 全部评论（{{ commentCount }}）</div>
              <div v-if="comments.length === 0" class="no-comments">
                <div class="no-comments-icon">📝</div>
                <div>暂无评论，快来发表第一条评论吧！</div>
              </div>
              <div v-for="comment in comments" :key="comment.id" class="comment-item">
                <div class="comment-avatar" :style="{ background: getUserColor(comment.username) }">
                  {{ getUserInitial(comment.username) }}
                </div>
                <div class="comment-body">
                  <div class="comment-meta">
                    <span class="comment-user">{{ comment.username }}</span>
                    <div class="comment-stars">
                      <span v-for="n in 5" :key="n" class="star" :class="{ filled: n <= comment.rating }">★</span>
                    </div>
                    <span class="comment-time">{{ formatDateTime(comment.created_at) }}</span>
                    <button v-if="isAdmin" class="delete-comment-btn" @click="removeComment(comment)">🗑️ 删除</button>
                  </div>
                  <div class="comment-content">{{ comment.content }}</div>
                </div>
              </div>
            </div>
          </div>

          <!-- 结果预览面板 -->
          <div v-else-if="activeTab === 'preview'" class="preview-panel">
            <div v-if="pdfPreviewUrl" class="pdf-preview-wrapper">
              <embed :src="pdfPreviewUrl" class="pdf-embed" type="application/pdf" />
            </div>
            <div v-else class="no-preview">
              <div class="no-preview-icon">📄</div>
              <div>暂无 PDF 文件可预览</div>
              <div class="no-preview-tip">任务执行成功后，如输出目录包含 PDF 文件将自动显示</div>
            </div>
          </div>

          <!-- 使用指南 -->
          <div v-else-if="activeTab === 'guide'" class="guide-panel">
            <div v-if="toolInfo.guide" class="guide-content" v-html="toolInfo.guide"></div>
            <div v-else>
              <h3>使用指南</h3>
              <ol>
                <li>在右侧"参数设置"标签页配置分析参数</li>
                <li>点击"执行"按钮开始分析任务</li>
                <li>在"日志"标签页查看执行进度</li>
                <li>任务完成后在"结果"标签页下载报告</li>
              </ol>
            </div>
          </div>

          <!-- 默认图表展示 -->
          <div v-else class="chart-wrapper">
            <ViolinBoxPlot />
          </div>
        </div>
      </div>

      <!-- 右侧配置区 -->
      <div class="right-panel">
        <!-- 右侧标签页 -->
        <div class="right-tabs">
          <div v-for="tab in tabs" :key="tab.key" 
               :class="['tab', { active: activeRightTab === tab.key }]"
               @click="activeRightTab = tab.key">
            {{ tab.label }}
          </div>
        </div>

        <!-- 标签内容 -->
        <div class="tab-content">
          <!-- 参数设置标签 -->
          <div v-if="activeRightTab === 'params'" class="params-panel">
            <div v-if="toolInfo.params && toolInfo.params.length > 0" class="params-form">
              <div v-for="param in toolInfo.params" :key="param.name" class="param-item">
                <label :class="['param-label', { required: param.required }]">
                  {{ param.label }}
                  <span v-if="param.required" class="required-mark">*</span>
                </label>
                
                <!-- 文本输入 -->
                <input v-if="param.type === 'text' || param.type === 'string'" 
                       v-model="params[param.name]" 
                       type="text" 
                       class="param-input"
                       :placeholder="param.placeholder || ''">
                
                <!-- 数字输入 -->
                <input v-else-if="param.type === 'number'" 
                       v-model.number="params[param.name]" 
                       type="number" 
                       class="param-input"
                       :step="param.step || 'any'"
                       :min="param.min"
                       :max="param.max"
                       :placeholder="param.placeholder || ''">
                
                <!-- 下拉选择 -->
                <select v-else-if="param.type === 'select'" v-model="params[param.name]" class="param-select">
                  <option v-for="opt in param.options" :key="opt.value || opt" :value="opt.value || opt">
                    {{ opt.label || opt }}
                  </option>
                </select>

                <!-- SCI配色选择 -->
                <div v-else-if="param.type === 'color'" class="param-color-picker">
                  <div class="color-blocks-row">
                    <span
                      v-for="(hex, i) in getColorOptions(param)"
                      :key="i"
                      class="color-picker-dot"
                      :class="{ active: params[param.name] === hex }"
                      :style="{ background: hex }"
                      @click="params[param.name] = hex"
                      :title="hex"
                    ></span>
                  </div>
                  <div class="custom-color-row">
                    <input type="color" v-model="params[param.name]" class="native-color-input" />
                    <input type="text" v-model="params[param.name]" class="hex-input" placeholder="#FFFFFF" maxlength="7" />
                  </div>
                </div>

                <!-- 配色方案选择 -->
                <div v-else-if="param.type === 'palette'" class="param-palette-picker">
                  <div class="custom-select">
                    <div class="select-trigger" @click="togglePaletteDropdown(param.name)">
                      <div class="preview-bar">
                        <span
                          v-for="(c, i) in getPalettePreviewColors(param).slice(0,3)"
                          :key="i"
                          class="preview-block"
                          :style="{ background: c }"
                        ></span>
                      </div>
                      <span class="trigger-text">{{ getPaletteTriggerText(param) }}</span>
                      <span>▼</span>
                    </div>
                    <div class="select-dropdown" :class="{ show: paletteDropdownOpen === param.name }">
                      <div
                        v-for="(palette, pIdx) in param.options"
                        :key="pIdx"
                        class="select-option"
                        @click="selectPaletteOption(param.name, pIdx, palette.colors.join(','))"
                      >
                        <div class="option-preview-bar">
                          <span
                            v-for="(c, i) in palette.colors.slice(0,3)"
                            :key="i"
                            class="option-preview-block"
                            :style="{ background: c }"
                          ></span>
                        </div>
                        <span>{{ palette.name }}</span>
                      </div>
                      <div class="select-option" @click="selectPaletteOption(param.name, 'custom', '')">
                        <span>🔧 自定义配色</span>
                      </div>
                    </div>
                  </div>
                  <div v-if="paletteStates[param.name] && paletteStates[param.name].selectedIndex === 'custom'" class="custom-palette-wrap">
                    <div class="custom-palette-colors">
                      <div
                        v-for="(c, i) in paletteStates[param.name].customColors"
                        :key="i"
                        class="custom-palette-item"
                      >
                        <input
                          type="color"
                          :value="c"
                          @input="updatePaletteCustomColor(param.name, i, $event.target.value)"
                        />
                        <span class="palette-hex">{{ c }}</span>
                        <button class="palette-del-btn" @click="removePaletteCustomColor(param.name, i)">×</button>
                      </div>
                      <button class="palette-add-btn" @click="addPaletteCustomColor(param.name)">+ 颜色</button>
                    </div>
                  </div>
                  <div class="palette-value-line">
                    <span class="palette-value-label">当前值：</span>
                    <code>{{ params[param.name] || '未选择' }}</code>
                  </div>
                </div>
                
                <!-- 文件选择（参数中的文件） -->
                <div v-else-if="param.type === 'file'" class="param-file">
                  <input type="file" @change="(e) => handleParamFile(e, param.name)" class="param-file-input" :disabled="uploadingFiles[param.name]">
                  <div v-if="uploadingFiles[param.name]" class="upload-progress-wrapper">
                    <div class="upload-progress-bar">
                      <div class="upload-progress-fill" :style="{ width: (uploadProgress[param.name] || 0) + '%' }"></div>
                    </div>
                    <div class="upload-progress-text">{{ uploadProgress[param.name] || 0 }}%</div>
                  </div>
                  <span v-else-if="params[param.name]" class="file-selected">
                    📎 {{ formatFileName(params[param.name]) }}
                  </span>
                </div>
                
                <!-- 开关 -->
                <label v-else-if="param.type === 'checkbox' || param.type === 'switch'" class="param-switch">
                  <input type="checkbox" v-model="params[param.name]">
                  <span class="switch-slider"></span>
                </label>
                
                <!-- 文本域 -->
                <textarea v-else-if="param.type === 'textarea'" 
                          v-model="params[param.name]" 
                          class="param-textarea"
                          :rows="param.rows || 3"
                          :placeholder="param.placeholder || ''"></textarea>
                
                <!-- 参数说明 -->
                <div v-if="param.description" class="param-desc">{{ param.description }}</div>
              </div>
            </div>
            <div v-else class="no-params">该工具无需配置参数</div>

            <div class="params-actions">
              <button class="primary-btn" @click="saveParams">保存参数</button>
              <button class="ghost-btn" @click="resetParams">重置</button>
            </div>
          </div>

          <!-- 日志标签 -->
          <div v-if="activeRightTab === 'logs'" class="logs-panel-right">
            <div v-if="logs.length > 0" class="logs-list">
              <div v-for="(log, index) in logs" :key="index" :class="['log-entry', log.type]">
                <span class="log-time">{{ formatTime(log.time) }}</span>
                <span class="log-message">{{ log.message }}</span>
              </div>
            </div>
            <div v-else class="empty-logs">暂无日志</div>
            <button v-if="hasMoreLogs" class="load-more-btn" @click="loadMoreLogs">加载更多</button>
          </div>

          <!-- 结果标签 -->
          <div v-if="activeRightTab === 'result'" class="result-panel-right">
            <div v-if="!taskInfo" class="no-task">请先创建并执行任务</div>
            <div v-else class="task-info">
              <div class="info-item">
                <span class="label">任务ID:</span>
                <span class="value">{{ taskInfo.taskId || taskInfo.id }}</span>
              </div>
              <div class="info-item">
                <span class="label">状态:</span>
                <span :class="['value', 'status', taskInfo.status]">{{ getStatusText(taskInfo.status) }}</span>
              </div>
              <div class="info-item">
                <span class="label">进度:</span>
                <div class="progress-bar">
                  <div class="progress-fill" :style="{ width: taskInfo.progress + '%' }"></div>
                </div>
                <span class="progress-text">{{ taskInfo.progress }}%</span>
              </div>
              <div v-if="taskInfo.status === 'SUCCESS'" class="result-actions-right">
                <div v-if="taskInfo.outputDir" class="output-dir-info">
                  <span class="label">输出目录：</span>
                  <code>{{ taskInfo.outputDir }}</code>
                </div>
                <button class="primary-btn" @click="downloadResult">⬇ 下载文件(.zip)</button>
              </div>
              <div v-if="taskInfo.status === 'FAILED'" class="error-actions">
                <button class="primary-btn" @click="retryTask">重新执行</button>
              </div>
              <!-- 结果文件列表 -->
              <div v-if="taskInfo.result_files && taskInfo.result_files.length > 0" class="result-files">
                <div class="result-files-title">📁 结果文件</div>
                <div v-for="(file, idx) in taskInfo.result_files" :key="idx" class="result-file-item">
                  <span class="result-file-name">{{ file.name }}</span>
                  <span class="result-file-size">{{ formatFileSize(file.size) }}</span>
                  <button class="download-file-btn" @click="downloadResultFileItem(file.path)">下载</button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 底部执行按钮 -->
        <div class="bottom-actions">
          <button 
            class="primary-btn full" 
            :class="{ 'danger-btn': taskInfo && (taskInfo.status === 'RUNNING' || taskInfo.status === 'PENDING') }"
            @click="handleTaskAction" 
            :disabled="loading && (!taskInfo || (taskInfo.status !== 'RUNNING' && taskInfo.status !== 'PENDING'))"
          >
            <span v-if="loading && (!taskInfo || (taskInfo.status !== 'RUNNING' && taskInfo.status !== 'PENDING'))" class="loading-icon">⏳</span>
            {{ taskActionText }}
          </button>
        </div>
      </div>
    </div>

    <!-- 下载进度条 -->
    <div v-if="isDownloading" class="download-overlay">
      <div class="download-panel">
        <div class="download-title">⬇️ 正在下载</div>
        <div class="download-filename">{{ downloadFileName }}</div>
        <div class="progress-track">
          <div class="progress-fill-bar" :style="{ width: downloadProgress + '%' }"></div>
        </div>
        <div class="progress-text">{{ downloadProgress }}%</div>
      </div>
    </div>

    <!-- Toast 提示 -->
    <div v-if="showToast" :class="['toast', toastType]">
      {{ toastMessage }}
    </div>

    <!-- 登录弹窗 -->
    <LoginDialog :visible.sync="showLoginDialog" @success="onLoginSuccess" @switch-register="showRegisterDialog = true" />
    <!-- 注册弹窗 -->
    <RegisterDialog :visible.sync="showRegisterDialog" @switch-login="showLoginDialog = true" />
  </div>
</template>

<script>
import { 
  getToolDetail, 
  uploadDataset, 
  pasteDataset,
  listServerFiles,
  selectServerFile as selectServerFileApi,
  createTask, 
  getTaskStatus, 
  getTaskLogs, 
  downloadResult as downloadResultApi,
  downloadTaskData,
  uploadToolFile,
  downloadResultFile,
  getTaskResultTree,
  cancelTask,
  getToolComments,
  addToolComment,
  deleteToolComment
} from '../api'
import { isLoginRequired } from '../utils/auth'
import ViolinBoxPlot from '../components/ViolinBoxPlot.vue'
import LoginDialog from '../components/LoginDialog.vue'
import RegisterDialog from '../components/RegisterDialog.vue'

export default {
  name: 'ToolDetail',
  props: ['id'],
  components: {
    ViolinBoxPlot,
    LoginDialog,
    RegisterDialog
  },
  data() {
    return {
      toolInfo: {},
      defaultColorOptions: ['#E64B35', '#4DBBD5', '#00A087', '#3C5488', '#F39B7F', '#8491B4', '#91D1C2', '#DC0000', '#7E6148', '#B09C85'],
      activeTab: 'guide', // guide, result, logs, comments, preview
      tabs: [
        { key: 'params', label: '⚙️ 参数设置' },
        { key: 'logs', label: '📋 日志' },
        { key: 'result', label: '📊 结果' }
      ],
      activeRightTab: 'params',
      
      // 评论
      comments: [],
      commentCount: 0,
      avgRating: 5,
      newCommentContent: '',
      newCommentRating: 5,
      hoverRating: 0,
      isAdmin: false,
      
      // PDF 预览
      pdfPreviewUrl: '',
      
      // 数据输入
      inputMethod: 'upload', // upload, paste, server
      uploadedFile: null,
      datasetInfo: null,
      pastedData: '',
      
      // 服务器文件
      serverFiles: [],
      serverCurrentPath: '',
      selectedServerFile: null,
      
      // 参数
      params: {},
      paramFiles: {},
      uploadingFiles: {},
      uploadProgress: {},
      paletteStates: {},
      paletteDropdownOpen: null,
      
      // 任务
      taskInfo: null,
      logs: [],
      hasMoreLogs: false,
      pollInterval: null,
      
      // 状态
      loading: false,
      dragOver: false,
      showToast: false,
      toastMessage: '',
      toastType: 'success',
      
      // 下载进度
      isDownloading: false,
      downloadProgress: 0,
      downloadFileName: '',
      // 登录弹窗
      showLoginDialog: false,
      showRegisterDialog: false,
      pendingAction: null
    }
  },
  computed: {
    canPreviewLog() {
      return this.taskInfo && this.taskInfo.status !== 'PENDING'
    },
    canDownload() {
      return this.taskInfo && this.taskInfo.status === 'SUCCESS'
    },
    isDynamicTool() {
      // 有 script_path 的为动态工具，不需要强制上传数据集
      return !!this.toolInfo.script_path
    },
    taskActionText() {
      if (!this.taskInfo) return '执行分析'
      if (this.taskInfo.status === 'RUNNING' || this.taskInfo.status === 'PENDING') return '结束任务'
      if (this.taskInfo.status === 'SUCCESS' || this.taskInfo.status === 'FAILED' || this.taskInfo.status === 'CANCELLED') return '查看结果'
      return '执行分析'
    },
    isTaskRunning() {
      return this.taskInfo && (this.taskInfo.status === 'RUNNING' || this.taskInfo.status === 'PENDING')
    }
  },
  async mounted() {
    await this.loadToolInfo()
    this.initParams()
    this.loadComments()
    this.checkIsAdmin()
    document.addEventListener('click', this.handleDocumentClick)
  },
  beforeDestroy() {
    if (this.pollInterval) {
      clearInterval(this.pollInterval)
    }
    document.removeEventListener('click', this.handleDocumentClick)
  },
  methods: {
    async loadToolInfo() {
      try {
        const res = await getToolDetail(this.id)
        if (res.code === 200) {
          this.toolInfo = res.data
          this.initParams()
        }
      } catch (error) {
        this.showToastMessage('加载工具信息失败', 'error')
      }
    },

    async loadComments() {
      try {
        const res = await getToolComments(parseInt(this.id))
        if (res.code === 200) {
          this.comments = res.data.comments || []
          this.commentCount = res.data.total || 0
          this.avgRating = res.data.avgRating || 5
        }
      } catch (error) {
        console.error('加载评论失败:', error)
      }
    },

    async submitComment() {
      if (!this.newCommentContent.trim()) {
        this.showToastMessage('请输入评论内容', 'warning')
        return
      }
      try {
        const res = await addToolComment(parseInt(this.id), {
          content: this.newCommentContent.trim(),
          rating: this.newCommentRating
        })
        if (res.code === 200) {
          this.showToastMessage('评论成功', 'success')
          this.newCommentContent = ''
          this.newCommentRating = 5
          await this.loadComments()
        }
      } catch (error) {
        this.showToastMessage('评论失败', 'error')
      }
    },

    async removeComment(comment) {
      if (!confirm('确定要删除这条评论吗？')) return
      try {
        const res = await deleteToolComment(parseInt(this.id), comment.id)
        if (res.code === 200) {
          this.showToastMessage('评论已删除', 'success')
          await this.loadComments()
        }
      } catch (error) {
        this.showToastMessage('删除失败', 'error')
      }
    },

    checkIsAdmin() {
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}')
      this.isAdmin = userInfo.role === 'admin' || userInfo.username === 'admin'
    },

    loadPdfPreview() {
      if (this.taskInfo && this.taskInfo.status === 'SUCCESS') {
        const taskId = this.taskInfo.taskId || this.taskInfo.id
        const token = localStorage.getItem('token')
        let url = `/api/tasks/${taskId}/result/preview-pdf`
        if (token) {
          url += `?token=${token}`
        }
        this.pdfPreviewUrl = url
      } else {
        this.pdfPreviewUrl = ''
      }
    },

    setRating(rating) {
      this.newCommentRating = rating
    },

    getUserColor(username) {
      const colors = ['#409eff', '#67c23a', '#e6a23c', '#f56c6c', '#909399', '#8e44ad', '#16a085']
      let hash = 0
      for (let i = 0; i < username.length; i++) {
        hash = username.charCodeAt(i) + ((hash << 5) - hash)
      }
      return colors[Math.abs(hash) % colors.length]
    },

    getUserInitial(username) {
      return username ? username.charAt(0).toUpperCase() : 'U'
    },

    initParams() {
      if (this.toolInfo.params && Array.isArray(this.toolInfo.params)) {
        this.params = {}
        this.paramFiles = {}
        this.paletteStates = {}
        this.toolInfo.params.forEach(param => {
          if (param.type === 'checkbox' || param.type === 'switch' || param.type === 'flag') {
            this.params[param.name] = param.default || false
          } else if (param.type === 'number' || param.type === 'int' || param.type === 'float') {
            this.params[param.name] = param.default !== undefined ? param.default : ''
          } else if (param.type === 'select') {
            const options = param.options || []
            this.params[param.name] = param.default || (options.length > 0 ? (options[0].value || options[0]) : '')
          } else if (param.type === 'color') {
            this.params[param.name] = param.default || ''
          } else if (param.type === 'palette') {
            this.params[param.name] = param.default || ''
            let selectedIndex = null
            let customColors = ['#ff0000', '#00ff00', '#0000ff']
            if (param.default) {
              let matched = false
              if (param.options && Array.isArray(param.options)) {
                for (let i = 0; i < param.options.length; i++) {
                  if (param.options[i].colors.join(',') === param.default) {
                    selectedIndex = i
                    matched = true
                    break
                  }
                }
              }
              if (!matched) {
                selectedIndex = 'custom'
                customColors = param.default.split(',').filter(c => c)
              }
            }
            this.$set(this.paletteStates, param.name, { selectedIndex, customColors })
          } else {
            this.params[param.name] = param.default || ''
          }
        })
      }
    },

    getColorOptions(param) {
      if (param.options && param.options.length > 0) {
        return param.options
      }
      return this.defaultColorOptions
    },

    // ========== 配色方案相关方法 ==========
    togglePaletteDropdown(paramName) {
      this.paletteDropdownOpen = this.paletteDropdownOpen === paramName ? null : paramName
    },
    selectPaletteOption(paramName, index, colorsStr) {
      this.paletteDropdownOpen = null
      const state = this.paletteStates[paramName] || { selectedIndex: null, customColors: ['#ff0000', '#00ff00', '#0000ff'] }
      state.selectedIndex = index
      if (index === 'custom') {
        this.params[paramName] = state.customColors.join(',')
      } else {
        this.params[paramName] = colorsStr
      }
      this.$set(this.paletteStates, paramName, state)
    },
    getPalettePreviewColors(param) {
      const val = this.params[param.name]
      if (!val) return []
      return val.split(',').filter(c => c)
    },
    getPaletteTriggerText(param) {
      const val = this.params[param.name]
      if (!val) return '请选择配色'
      const state = this.paletteStates[param.name]
      if (state && state.selectedIndex === 'custom') return '自定义配色'
      if (param.options && Array.isArray(param.options)) {
        for (const pal of param.options) {
          if (pal.colors.join(',') === val) {
            return pal.name
          }
        }
      }
      return '自定义配色'
    },
    updatePaletteCustomColor(paramName, idx, color) {
      const state = this.paletteStates[paramName]
      if (!state) return
      state.customColors[idx] = color
      this.params[paramName] = state.customColors.join(',')
      this.$set(this.paletteStates, paramName, { ...state })
    },
    addPaletteCustomColor(paramName) {
      const state = this.paletteStates[paramName] || { selectedIndex: 'custom', customColors: [] }
      state.customColors.push('#cccccc')
      state.selectedIndex = 'custom'
      this.params[paramName] = state.customColors.join(',')
      this.$set(this.paletteStates, paramName, { ...state })
    },
    removePaletteCustomColor(paramName, idx) {
      const state = this.paletteStates[paramName]
      if (!state) return
      state.customColors.splice(idx, 1)
      this.params[paramName] = state.customColors.join(',')
      this.$set(this.paletteStates, paramName, { ...state })
    },

    handleDocumentClick(e) {
      const selectors = document.querySelectorAll('.param-palette-picker .custom-select')
      let inside = false
      selectors.forEach(el => {
        if (el.contains(e.target)) inside = true
      })
      if (!inside) {
        this.paletteDropdownOpen = null
      }
    },

    goBack() {
      this.$emit('back')
    },

    showGuide() {
      this.activeTab = 'guide'
    },

    // ============ 文件上传相关 ============
    dragOverFile(e) {
      e.preventDefault()
    },

    handleDrop(e) {
      e.preventDefault()
      this.dragOver = false
      const files = e.dataTransfer.files
      if (files.length > 0) {
        this.handleFile(files[0])
      }
    },

    handleFileSelect(e) {
      const file = e.target.files[0]
      if (file) {
        this.handleFile(file)
      }
    },

    async handleFile(file) {
      const validTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel',
        'text/csv',
        'application/vnd.ms-excel.sheet.macroEnabled.12'
      ]
      
      if (!validTypes.includes(file.type) && !file.name.match(/\.(xlsx|xls|csv)$/i)) {
        this.showToastMessage('请上传 CSV 或 Excel 文件', 'error')
        return
      }

      this.loading = true
      try {
        const formData = new FormData()
        formData.append('file', file)

        const res = await uploadDataset(formData)

        if (res.code === 200) {
          this.uploadedFile = file
          this.datasetInfo = res.data
          this.showToastMessage('上传成功', 'success')
        }
      } catch (error) {
        this.showToastMessage('上传失败：' + (error.response?.data?.message || error.message), 'error')
      } finally {
        this.loading = false
      }
    },

    removeFile() {
      this.uploadedFile = null
      this.datasetInfo = null
      this.$refs.fileInput.value = ''
    },

    // ============ 粘贴数据 ============
    async handlePasteData() {
      if (!this.pastedData.trim()) {
        this.showToastMessage('请输入数据', 'warning')
        return
      }

      this.loading = true
      try {
        const res = await pasteDataset({
          data: this.pastedData,
          filename: 'pasted_data.csv'
        })

        if (res.code === 200) {
          this.datasetInfo = res.data
          this.uploadedFile = { name: 'pasted_data.csv', size: 0 }
          this.showToastMessage('数据导入成功', 'success')
        }
      } catch (error) {
        this.showToastMessage('导入失败：' + (error.response?.data?.message || error.message), 'error')
      } finally {
        this.loading = false
      }
    },

    // ============ 服务器文件 ============
    async loadServerFiles(path = '') {
      this.loading = true
      try {
        const res = await listServerFiles(path)
        if (res.code === 200) {
          this.serverFiles = res.data
          this.serverCurrentPath = path
        }
      } catch (error) {
        this.showToastMessage('加载文件列表失败', 'error')
      } finally {
        this.loading = false
      }
    },

    selectServerFile(file) {
      if (file.isDirectory) {
        this.loadServerFiles(file.path)
      } else {
        this.selectedServerFile = file.path
      }
    },

    goBackServerDir() {
      const parts = this.serverCurrentPath.split('/').filter(p => p)
      parts.pop()
      const parentPath = parts.join('/')
      this.loadServerFiles(parentPath)
    },

    async confirmServerFile() {
      if (!this.selectedServerFile) return

      this.loading = true
      try {
        const res = await selectServerFileApi(this.selectedServerFile)
        if (res.code === 200) {
          this.datasetInfo = res.data
          this.uploadedFile = { name: res.data.filename, size: 0 }
          this.showToastMessage('文件选择成功', 'success')
        }
      } catch (error) {
        this.showToastMessage('选择文件失败：' + (error.response?.data?.message || error.message), 'error')
      } finally {
        this.loading = false
      }
    },

    // ============ 参数相关 ============
    async handleParamFile(e, paramName) {
      const file = e.target.files[0]
      if (!file) return
      
      this.$set(this.uploadingFiles, paramName, true)
      this.$set(this.uploadProgress, paramName, 0)
      try {
        const formData = new FormData()
        formData.append('file', file)
        
        const res = await uploadToolFile(this.id, paramName, formData, (progressEvent) => {
          if (progressEvent.lengthComputable) {
            const percent = Math.round((progressEvent.loaded / progressEvent.total) * 100)
            this.$set(this.uploadProgress, paramName, percent)
          }
        })
        if (res.code === 200) {
          this.$set(this.paramFiles, paramName, file)
          this.params[paramName] = res.data.savedPath
          this.showToastMessage(`文件上传成功: ${file.name}`, 'success')
        } else {
          this.showToastMessage(res.message || '上传失败', 'error')
        }
      } catch (error) {
        console.error('Upload error:', error)
        const msg = error.response?.data?.message || error.message || '上传失败'
        this.showToastMessage(msg, 'error')
      } finally {
        this.$set(this.uploadingFiles, paramName, false)
        this.$set(this.uploadProgress, paramName, 0)
      }
    },
    formatFileName(fullPath) {
      if (!fullPath) return ''
      return fullPath.split('/').pop()
    },

    saveParams() {
      this.showToastMessage('参数已保存', 'success')
    },

    resetParams() {
      this.initParams()
      this.showToastMessage('参数已重置', 'info')
    },

    // ============ 任务相关 ============
    handleTaskAction() {
      if (this.isTaskRunning) {
        this.stopTask()
      } else if (this.taskInfo && (this.taskInfo.status === 'SUCCESS' || this.taskInfo.status === 'FAILED' || this.taskInfo.status === 'CANCELLED')) {
        this.activeTab = 'result'
        this.activeRightTab = 'result'
      } else {
        this.runTask()
      }
    },

    async runTask() {
      // 检查登录状态（2026年10月30日之后必须登录）
      const token = localStorage.getItem('token')
      if (isLoginRequired() && !token) {
        this.showToastMessage('请先登录后再执行任务', 'warning')
        this.showLoginDialog = true
        this.pendingAction = 'runTask'
        return
      }

      // 验证必填参数
      if (this.toolInfo.params) {
        for (const param of this.toolInfo.params) {
          if (param.required && (this.params[param.name] === '' || this.params[param.name] === null || this.params[param.name] === undefined)) {
            this.showToastMessage(`请填写必填参数: ${param.label}`, 'warning')
            this.activeRightTab = 'params'
            return
          }
        }
      }

      this.loading = true
      try {
        const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}')
        const res = await createTask({
          toolId: parseInt(this.id),
          datasetId: '',
          params: this.params,
          userId: userInfo.user_id || 0
        })

        if (res.code === 200) {
          this.taskInfo = res.data
          this.showToastMessage('任务已创建，开始执行', 'success')
          this.activeRightTab = 'logs'
          this.startTaskPolling()
        }
      } catch (error) {
        this.showToastMessage('创建任务失败：' + (error.response?.data?.message || error.message), 'error')
      } finally {
        this.loading = false
      }
    },

    async stopTask() {
      if (!this.taskInfo || !this.taskInfo.taskId) return
      
      if (!confirm('确定要终止当前任务吗？')) return
      
      try {
        const res = await cancelTask(this.taskInfo.taskId)
        if (res.code === 200) {
          this.showToastMessage('任务已终止', 'info')
          if (this.pollInterval) {
            clearInterval(this.pollInterval)
            this.pollInterval = null
          }
          this.taskInfo = { ...this.taskInfo, status: 'CANCELLED', progress: 0 }
        }
      } catch (error) {
        this.showToastMessage('终止任务失败：' + (error.response?.data?.message || error.message), 'error')
      }
    },

    startTaskPolling() {
      if (this.pollInterval) {
        clearInterval(this.pollInterval)
      }
      
      this.pollInterval = setInterval(() => {
        this.checkTaskStatus()
      }, 2000)
    },

    async checkTaskStatus() {
      if (!this.taskInfo || !this.taskInfo.taskId) return

      try {
        const res = await getTaskStatus(this.taskInfo.taskId)
        if (res.code === 200) {
          this.taskInfo = res.data

          if (res.data.status === 'SUCCESS' || res.data.status === 'FAILED' || res.data.status === 'CANCELLED') {
            clearInterval(this.pollInterval)
            this.pollInterval = null
            if (res.data.status === 'SUCCESS') {
              this.showToastMessage('任务执行成功！', 'success')
            } else if (res.data.status === 'FAILED') {
              this.showToastMessage('任务执行失败', 'error')
            } else if (res.data.status === 'CANCELLED') {
              this.showToastMessage('任务已终止', 'info')
            }
          }
          
          // 加载日志
          this.loadLogs()
        }
      } catch (error) {
        console.error('检查任务状态失败:', error)
      }
    },

    async loadLogs() {
      if (!this.taskInfo || !this.taskInfo.taskId) return

      try {
        const res = await getTaskLogs(this.taskInfo.taskId)
        if (res.code === 200) {
          this.logs = res.data.logs
          this.hasMoreLogs = res.data.hasMore
        }
      } catch (error) {
        console.error('加载日志失败:', error)
      }
    },

    loadMoreLogs() {
      this.loadLogs()
    },

    retryTask() {
      this.runTask()
    },

    // ============ 下载相关（带进度条） ============
    downloadWithProgress(url, filename) {
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest()
        this.isDownloading = true
        this.downloadProgress = 0
        this.downloadFileName = filename
        
        xhr.open('GET', url, true)
        xhr.responseType = 'blob'
        
        xhr.onprogress = (event) => {
          if (event.lengthComputable) {
            this.downloadProgress = Math.round((event.loaded / event.total) * 100)
          }
        }
        
        xhr.onload = () => {
          this.isDownloading = false
          if (xhr.status === 200) {
            const blob = new Blob([xhr.response])
            const downloadUrl = window.URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = downloadUrl
            a.download = filename
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
            window.URL.revokeObjectURL(downloadUrl)
            resolve()
          } else {
            reject(new Error(`下载失败: ${xhr.status}`))
          }
        }
        
        xhr.onerror = () => {
          this.isDownloading = false
          reject(new Error('下载失败'))
        }
        
        xhr.onabort = () => {
          this.isDownloading = false
          reject(new Error('下载已取消'))
        }
        
        xhr.send()
      })
    },
    
    async downloadResult() {
      if (!this.canDownload) {
        this.showToastMessage('结果尚未生成', 'warning')
        return
      }
      
      const taskId = this.taskInfo.taskId || this.taskInfo.id
      const url = `/api/tasks/${taskId}/result/download`
      
      try {
        await this.downloadWithProgress(url, `result_${taskId}.zip`)
        this.showToastMessage('下载成功', 'success')
      } catch (error) {
        this.showToastMessage('下载失败', 'error')
      }
    },

    async downloadResultFileItem(filePath) {
      if (!this.canDownload) {
        this.showToastMessage('结果尚未生成', 'warning')
        return
      }
      
      const taskId = this.taskInfo.taskId || this.taskInfo.id
      const encodedPath = encodeURIComponent(filePath)
      const url = `/api/tasks/${taskId}/result/download?path=${encodedPath}`
      const filename = filePath.split('/').pop()
      
      try {
        await this.downloadWithProgress(url, filename)
        this.showToastMessage('下载成功', 'success')
      } catch (error) {
        this.showToastMessage('下载失败', 'error')
      }
    },

    downloadDataset() {
      if (!this.datasetInfo) return
      
      // 生成CSV下载
      const columns = this.datasetInfo.columns
      const rows = this.datasetInfo.previewData
      let csv = columns.join(',') + '\n'
      rows.forEach(row => {
        csv += columns.map(col => row[col] || '').join(',') + '\n'
      })
      
      const blob = new Blob([csv], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = this.datasetInfo.filename || 'data.csv'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)
    },

    // ============ 工具方法 ============
    formatFileSize(bytes) {
      if (bytes === 0) return '0 B'
      const k = 1024
      const sizes = ['B', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    },

    formatTime(timestamp) {
      if (!timestamp) return '-'
      const date = new Date(timestamp)
      return date.toLocaleTimeString('zh-CN')
    },

    formatDateTime(timestamp) {
      if (!timestamp) return '-'
      const date = new Date(timestamp)
      return date.toLocaleString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
    },

    getStatusText(status) {
      const statusMap = {
        'PENDING': '等待中',
        'RUNNING': '执行中',
        'SUCCESS': '成功',
        'FAILED': '失败'
      }
      return statusMap[status] || status
    },

    showToastMessage(message, type = 'success') {
      this.toastMessage = message
      this.toastType = type
      this.showToast = true
      setTimeout(() => {
        this.showToast = false
      }, 3000)
    },
    onLoginSuccess() {
      this.showLoginDialog = false
      // 通知父组件登录成功，更新右上角状态
      this.$emit('login-success')
      if (this.pendingAction === 'runTask') {
        this.pendingAction = null
        this.runTask()
      }
    }
  },
  watch: {
    inputMethod(val) {
      if (val === 'server' && this.serverFiles.length === 0) {
        this.loadServerFiles()
      }
    },
    taskInfo: {
      handler(val, oldVal) {
        if (val && val.status === 'SUCCESS' && (!oldVal || oldVal.status !== 'SUCCESS')) {
          this.loadPdfPreview()
        }
      },
      deep: true
    }
  }
}
</script>

<style scoped>
.analysis-container {
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f5f7fa;
}

.top-bar {
  height: 60px;
  background: #fff;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 20px;
  flex-shrink: 0;
}

.title {
  font-size: 18px;
  font-weight: bold;
  flex: 1;
  text-align: center;
}

.actions {
  display: flex;
  gap: 10px;
}

.main-layout {
  flex: 1;
  display: flex;
  overflow: hidden;
}

.left-panel {
  flex: 2;
  background: #fff;
  border-right: 1px solid #eee;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.chart-toolbar {
  display: flex;
  gap: 12px;
  padding: 15px 20px;
  border-bottom: 1px solid #eee;
  flex-wrap: wrap;
}

.content-area {
  flex: 1;
  overflow: auto;
  padding: 20px;
}

.result-panel,
.logs-panel,
.guide-panel {
  height: 100%;
}

.pdf-preview-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  gap: 20px;
}

.success-message {
  font-size: 18px;
  color: #67c23a;
  display: flex;
  align-items: center;
  gap: 10px;
}

.success-icon {
  font-size: 32px;
}

.result-actions {
  display: flex;
  gap: 15px;
}

.output-dir-info {
  background: #f1f5f9;
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 13px;
  color: #475569;
  margin-bottom: 12px;
  word-break: break-all;
}

.output-dir-info .label {
  font-weight: 600;
  color: #334155;
}

.output-dir-info code {
  background: #e2e8f0;
  padding: 2px 6px;
  border-radius: 4px;
  font-family: 'Fira Code', monospace;
  font-size: 12px;
  color: #0f172a;
}

.running-panel {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  gap: 20px;
}

.running-animation {
  font-size: 24px;
  animation: pulse 1.5s infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

.progress-info {
  font-size: 16px;
  color: #666;
}

.empty-result {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.no-result {
  font-size: 16px;
  color: #999;
}

.logs-container {
  font-family: monospace;
  font-size: 13px;
}

.log-item {
  padding: 8px 0;
  border-bottom: 1px solid #eee;
  display: flex;
  gap: 10px;
}

.log-time {
  color: #999;
  white-space: nowrap;
}

.log-type {
  font-weight: bold;
  min-width: 50px;
}

.log-item.info .log-type { color: #409eff; }
.log-item.success .log-type { color: #67c23a; }
.log-item.warning .log-type { color: #e6a23c; }
.log-item.error .log-type { color: #f56c6c; }

.empty-logs {
  text-align: center;
  color: #999;
  padding: 40px;
}

.guide-panel h3 {
  margin-bottom: 20px;
}

.guide-panel ol {
  line-height: 2;
  padding-left: 20px;
}

.guide-panel li {
  margin-bottom: 10px;
}

.guide-content {
  line-height: 1.8;
  color: #606266;
}

.guide-content h1,
.guide-content h2,
.guide-content h3,
.guide-content h4 {
  color: #303133;
  margin: 16px 0 10px;
}

.guide-content p {
  margin-bottom: 10px;
}

.guide-content ul,
.guide-content ol {
  padding-left: 20px;
  margin: 10px 0;
}

.guide-content li {
  margin-bottom: 8px;
}

.guide-content strong {
  color: #409eff;
}

.right-panel {
  flex: 1;
  background: #fafafa;
  display: flex;
  flex-direction: column;
  min-width: 350px;
  max-width: 450px;
}

.right-tabs {
  display: flex;
  border-bottom: 1px solid #eee;
  background: #fff;
}

.tab {
  flex: 1;
  text-align: center;
  padding: 12px 8px;
  cursor: pointer;
  font-size: 13px;
  transition: all 0.2s;
  border-bottom: 2px solid transparent;
}

.tab:hover {
  background: #f5f7fa;
}

.tab.active {
  border-bottom-color: #409eff;
  color: #409eff;
  font-weight: bold;
}

.tab-content {
  flex: 1;
  overflow: auto;
  padding: 15px;
}

/* 上传面板样式 */
.input-method-tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 15px;
}

.method-btn {
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #dcdfe6;
  background: #fff;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  transition: all 0.2s;
}

.method-btn:hover {
  border-color: #409eff;
  color: #409eff;
}

.method-btn.active {
  background: #409eff;
  border-color: #409eff;
  color: #fff;
}

.upload-area {
  border: 2px dashed #dcdfe6;
  border-radius: 8px;
  padding: 40px 20px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: 20px;
}

.upload-area:hover,
.upload-area.dragover {
  border-color: #409eff;
  background: #f5f7fa;
}

.upload-area.hasFile {
  border-style: solid;
  border-color: #67c23a;
  background: #f0f9eb;
}

.upload-icon {
  font-size: 48px;
  margin-bottom: 10px;
}

.upload-text {
  font-size: 16px;
  color: #606266;
  margin-bottom: 8px;
}

.upload-hint {
  font-size: 12px;
  color: #909399;
}

.file-info {
  display: flex;
  align-items: center;
  gap: 15px;
}

.file-icon {
  font-size: 36px;
}

.file-details {
  flex: 1;
  text-align: left;
}

.file-name {
  font-size: 14px;
  color: #303133;
  margin-bottom: 4px;
  word-break: break-all;
}

.file-size {
  font-size: 12px;
  color: #909399;
}

.remove-btn {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: none;
  background: #f56c6c;
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* 粘贴数据 */
.paste-textarea {
  width: 100%;
  padding: 10px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  font-family: monospace;
  font-size: 13px;
  resize: vertical;
  margin-bottom: 10px;
}

.paste-actions {
  display: flex;
  gap: 10px;
}

/* 服务器文件浏览器 */
.server-file-browser {
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background: #fff;
  margin-bottom: 10px;
}

.browser-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  border-bottom: 1px solid #eee;
  background: #f5f7fa;
}

.current-path {
  font-size: 13px;
  color: #606266;
}

.back-btn {
  padding: 4px 8px;
  font-size: 12px;
  border: 1px solid #dcdfe6;
  background: #fff;
  border-radius: 4px;
  cursor: pointer;
}

.file-list {
  max-height: 250px;
  overflow: auto;
}

.file-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px;
  cursor: pointer;
  border-bottom: 1px solid #f0f0f0;
  transition: background 0.2s;
}

.file-item:hover {
  background: #f5f7fa;
}

.file-item.selected {
  background: #e6f4ff;
}

.file-item .file-name {
  flex: 1;
  font-size: 13px;
}

.empty-files {
  padding: 40px;
  text-align: center;
  color: #999;
}

.server-actions {
  display: flex;
  gap: 10px;
}

/* 数据预览 */
.data-preview {
  margin-top: 20px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  background: #fff;
}

.preview-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  background: #f5f7fa;
  border-bottom: 1px solid #ebeef5;
}

.preview-title {
  font-size: 13px;
  font-weight: bold;
  color: #606266;
}

.preview-download {
  cursor: pointer;
  font-size: 16px;
}

.preview-table-wrapper {
  max-height: 200px;
  overflow: auto;
}

.preview-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.preview-table th,
.preview-table td {
  border: 1px solid #ebeef5;
  padding: 6px 10px;
  text-align: left;
}

.preview-table th {
  background: #f5f7fa;
  font-weight: bold;
}

/* 参数面板 */
.params-panel {
  padding: 10px 0;
}

.params-form {
  margin-bottom: 20px;
}

.param-item {
  margin-bottom: 15px;
}

.param-label {
  display: block;
  font-size: 13px;
  color: #606266;
  margin-bottom: 6px;
}

.param-label.required {
  font-weight: bold;
}

.required-mark {
  color: #f56c6c;
  margin-left: 4px;
}

.param-input,
.param-select,
.param-textarea {
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  font-size: 13px;
  transition: border-color 0.2s;
}

.param-input:focus,
.param-select:focus,
.param-textarea:focus {
  outline: none;
  border-color: #409eff;
}

.param-textarea {
  resize: vertical;
}

.param-desc {
  font-size: 12px;
  color: #909399;
  margin-top: 4px;
}

.param-color-picker {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.color-blocks-row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.color-picker-dot {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: 2px solid transparent;
  cursor: pointer;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.15);
  transition: all 0.2s;
}

.color-picker-dot:hover {
  transform: scale(1.15);
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.25);
}

.color-picker-dot.active {
  border-color: #1e293b;
  transform: scale(1.12);
  position: relative;
}

.color-picker-dot.active::after {
  content: '✓';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: #fff;
  font-size: 14px;
  font-weight: bold;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
  line-height: 1;
}

.custom-color-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.native-color-input {
  width: 44px;
  height: 32px;
  padding: 0;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  cursor: pointer;
}

.hex-input {
  width: 90px;
  padding: 5px 10px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  font-family: 'SFMono-Regular', Consolas, monospace;
  font-size: 13px;
  color: #333;
  text-align: center;
}

.hex-input:focus {
  outline: none;
  border-color: #409eff;
  box-shadow: 0 0 0 3px rgba(64, 158, 255, 0.12);
}

/* 配色方案选择器 */
.param-palette-picker {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.param-palette-picker .custom-select {
  position: relative;
  width: 100%;
}
.param-palette-picker .select-trigger {
  padding: 8px 12px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.param-palette-picker .preview-bar {
  display: flex;
  gap: 3px;
}
.param-palette-picker .preview-block {
  width: 14px;
  height: 14px;
  border-radius: 2px;
}
.param-palette-picker .trigger-text {
  flex: 1;
  font-size: 13px;
  color: #606266;
}
.param-palette-picker .select-dropdown {
  position: absolute;
  top: 110%;
  left: 0;
  right: 0;
  border: 1px solid #eee;
  border-radius: 4px;
  background: #fff;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
  display: none;
  z-index: 999;
  max-height: 300px;
  overflow-y: auto;
}
.param-palette-picker .select-dropdown.show {
  display: block;
}
.param-palette-picker .select-option {
  padding: 10px 12px;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}
.param-palette-picker .select-option:hover {
  background: #f5f7fa;
}
.param-palette-picker .option-preview-bar {
  display: flex;
  gap: 3px;
}
.param-palette-picker .option-preview-block {
  width: 14px;
  height: 14px;
  border-radius: 2px;
}
.custom-palette-wrap {
  margin-top: 4px;
  padding: 10px;
  background: #f5f7fa;
  border-radius: 4px;
  border: 1px dashed #dcdfe6;
}
.custom-palette-colors {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}
.custom-palette-item {
  display: flex;
  align-items: center;
  gap: 4px;
  background: #fff;
  padding: 4px 8px;
  border-radius: 4px;
  border: 1px solid #e4e7ed;
}
.custom-palette-item input[type="color"] {
  width: 28px;
  height: 28px;
  border: none;
  padding: 0;
  cursor: pointer;
}
.palette-hex {
  font-size: 11px;
  color: #909399;
  font-family: monospace;
}
.palette-del-btn {
  background: #fef0f0;
  color: #f56c6c;
  border: none;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  cursor: pointer;
  font-size: 12px;
  line-height: 1;
}
.palette-add-btn {
  background: #f0f9eb;
  color: #67c23a;
  border: 1px dashed #67c23a;
  padding: 4px 10px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
}
.palette-value-line {
  font-size: 12px;
  color: #909399;
}
.palette-value-line code {
  background: #f5f7fa;
  padding: 2px 6px;
  border-radius: 4px;
  font-family: monospace;
  font-size: 11px;
}

.param-switch {
  display: inline-flex;
  align-items: center;
  cursor: pointer;
}

.param-switch input {
  display: none;
}

.switch-slider {
  width: 40px;
  height: 20px;
  background: #dcdfe6;
  border-radius: 10px;
  position: relative;
  transition: background 0.2s;
}

.switch-slider::after {
  content: '';
  position: absolute;
  width: 16px;
  height: 16px;
  background: #fff;
  border-radius: 50%;
  top: 2px;
  left: 2px;
  transition: transform 0.2s;
}

.param-switch input:checked + .switch-slider {
  background: #409eff;
}

.param-switch input:checked + .switch-slider::after {
  transform: translateX(20px);
}

.param-file-input {
  width: 100%;
  padding: 6px 0;
}

.file-selected {
  font-size: 12px;
  color: #67c23a;
  margin-top: 4px;
  display: block;
}

.upload-progress-wrapper {
  margin-top: 8px;
}

.upload-progress-bar {
  width: 100%;
  height: 8px;
  background: #ebeef5;
  border-radius: 4px;
  overflow: hidden;
}

.upload-progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #409eff, #66b1ff);
  border-radius: 4px;
  transition: width 0.2s ease;
}

.upload-progress-text {
  font-size: 12px;
  color: #606266;
  margin-top: 4px;
  text-align: right;
}

.no-params {
  text-align: center;
  color: #909399;
  padding: 40px;
}

.params-actions {
  display: flex;
  gap: 10px;
}

/* 日志面板 */
.logs-panel-right {
  height: 100%;
}

.logs-list {
  font-family: monospace;
  font-size: 12px;
  max-height: 400px;
  overflow: auto;
}

.log-entry {
  padding: 6px 0;
  border-bottom: 1px solid #eee;
}

.log-entry .log-time {
  color: #999;
  margin-right: 10px;
}

.load-more-btn {
  width: 100%;
  padding: 10px;
  margin-top: 10px;
  border: 1px dashed #dcdfe6;
  background: #fff;
  cursor: pointer;
  border-radius: 4px;
}

/* 结果面板 */
.result-panel-right {
  padding: 10px 0;
}

.no-task {
  text-align: center;
  color: #909399;
  padding: 40px;
}

.task-info {
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  padding: 15px;
}

.info-item {
  display: flex;
  align-items: center;
  margin-bottom: 15px;
  gap: 10px;
}

.info-item .label {
  font-size: 13px;
  color: #606266;
  min-width: 60px;
}

.info-item .value {
  font-size: 13px;
  color: #303133;
}

.info-item .value.status {
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
}

.value.status.PENDING {
  background: #e6f4ff;
  color: #409eff;
}

.value.status.RUNNING {
  background: #fff7e6;
  color: #e6a23c;
}

.value.status.SUCCESS {
  background: #f0f9eb;
  color: #67c23a;
}

.value.status.FAILED {
  background: #fef0f0;
  color: #f56c6c;
}

.progress-bar {
  flex: 1;
  height: 8px;
  background: #ebeef5;
  border-radius: 4px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: #409eff;
  transition: width 0.3s;
}

.progress-text {
  font-size: 12px;
  color: #606266;
  min-width: 40px;
  text-align: right;
}

.result-actions-right,
.error-actions {
  display: flex;
  gap: 10px;
  margin-top: 20px;
  padding-top: 15px;
  border-top: 1px solid #eee;
}

/* 结果文件列表 */
.result-files {
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid #eee;
}

.result-files-title {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 10px;
}

.result-file-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  background: #f8fafc;
  border-radius: 6px;
  margin-bottom: 6px;
  font-size: 13px;
}

.result-file-name {
  flex: 1;
  color: #334155;
  word-break: break-all;
}

.result-file-size {
  color: #94a3b8;
  font-size: 12px;
  white-space: nowrap;
}

.download-file-btn {
  background: #e0f2fe;
  color: #0369a1;
  border: none;
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  transition: background 0.2s;
  white-space: nowrap;
}

.download-file-btn:hover {
  background: #bae6fd;
}

/* 底部操作 */
.bottom-actions {
  padding: 15px;
  border-top: 1px solid #eee;
  background: #fff;
}

/* 通用按钮样式 */
.tool-btn {
  background: #f5f5f5;
  border: 1px solid #ddd;
  padding: 8px 14px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  transition: all 0.2s;
}

.tool-btn:hover:not(:disabled) {
  background: #e6f4ff;
  border-color: #409eff;
}

.tool-btn.primary {
  background: #e6f4ff;
  border-color: #409eff;
  color: #409eff;
}

.tool-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.primary-btn {
  background: #409eff;
  color: #fff;
  border: none;
  padding: 8px 18px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.primary-btn:hover:not(:disabled) {
  background: #66b1ff;
}

.primary-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.primary-btn.full {
  width: 100%;
  justify-content: center;
  padding: 12px;
  font-size: 14px;
}

.primary-btn.danger-btn {
  background: #ef4444;
}

.primary-btn.danger-btn:hover:not(:disabled) {
  background: #dc2626;
}

.ghost-btn {
  background: #fff;
  border: 1px solid #dcdfe6;
  padding: 8px 18px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.ghost-btn:hover {
  border-color: #409eff;
  color: #409eff;
}

.danger-btn {
  background: #f56c6c;
  color: #fff;
  border: none;
  padding: 8px 18px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
}

.danger-btn:hover {
  background: #f78989;
}

.small {
  padding: 6px 12px;
  font-size: 12px;
}

.loading-icon {
  animation: rotate 1s linear infinite;
}

@keyframes rotate {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* 下载进度条 */
.download-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9998;
}

.download-panel {
  background: #fff;
  border-radius: 16px;
  padding: 32px 40px;
  width: 420px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  text-align: center;
}

.download-title {
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 8px;
}

.download-filename {
  font-size: 13px;
  color: #64748b;
  margin-bottom: 20px;
  word-break: break-all;
}

.progress-track {
  width: 100%;
  height: 10px;
  background: #e2e8f0;
  border-radius: 5px;
  overflow: hidden;
  margin-bottom: 10px;
}

.progress-fill-bar {
  height: 100%;
  background: linear-gradient(90deg, #0ea5e9, #0284c7);
  border-radius: 5px;
  transition: width 0.2s ease;
}

.progress-text {
  font-size: 14px;
  font-weight: 600;
  color: #0ea5e9;
}

/* Toast */
.toast {
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  padding: 12px 24px;
  border-radius: 4px;
  color: #fff;
  font-size: 14px;
  z-index: 9999;
  animation: fadeIn 0.3s;
}

.toast.success {
  background: #67c23a;
}

.toast.error {
  background: #f56c6c;
}

.toast.warning {
  background: #e6a23c;
}

.toast.info {
  background: #909399;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateX(-50%) translateY(-20px); }
  to { opacity: 1; transform: translateX(-50%) translateY(0); }
}

/* 图表区域 */
.chart-wrapper {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.icon {
  margin-right: 4px;
}

/* ============ 评论面板样式 ============ */

.comments-panel {
  height: 100%;
  overflow: auto;
  padding-right: 4px;
}

/* 评分概览 */
.comments-header {
  background: linear-gradient(135deg, #fff8f0 0%, #fff 100%);
  border: 1px solid #ffe4c7;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 20px;
  text-align: center;
}

.rating-score-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}

.rating-big-score {
  font-size: 48px;
  font-weight: 800;
  color: #f59e0b;
  line-height: 1;
}

.rating-big-stars {
  display: flex;
  gap: 4px;
}

.big-star {
  font-size: 24px;
  color: #e5e7eb;
  transition: color 0.2s;
}

.big-star.filled {
  color: #f59e0b;
}

.rating-total {
  font-size: 14px;
  color: #9ca3af;
  margin-top: 4px;
}

/* 评论表单 */
.comment-form {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
}

.form-title {
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 14px;
  padding-bottom: 10px;
  border-bottom: 1px solid #f3f4f6;
}

.rating-selector {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
  flex-wrap: wrap;
}

.rating-tip {
  font-size: 14px;
  color: #4b5563;
}

.star-input-group {
  display: flex;
  gap: 6px;
}

.star-input {
  font-size: 28px;
  color: #d1d5db;
  cursor: pointer;
  transition: all 0.15s ease;
  user-select: none;
  line-height: 1;
}

.star-input:hover {
  transform: scale(1.2);
}

.star-input.filled {
  color: #f59e0b;
}

.rating-text {
  font-size: 13px;
  color: #f59e0b;
  font-weight: 500;
  min-width: 56px;
}

.comment-textarea {
  width: 100%;
  padding: 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  resize: vertical;
  margin-bottom: 12px;
  box-sizing: border-box;
  font-family: inherit;
  line-height: 1.6;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.comment-textarea:focus {
  outline: none;
  border-color: #f59e0b;
  box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.1);
}

.comment-textarea::placeholder {
  color: #9ca3af;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
}

.submit-comment-btn {
  padding: 10px 24px;
  font-size: 14px;
  border-radius: 8px;
}

/* 评论列表 */
.comments-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.list-title {
  font-size: 15px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 4px;
}

.no-comments {
  text-align: center;
  color: #9ca3af;
  padding: 48px 20px;
  font-size: 14px;
  background: #f9fafb;
  border-radius: 12px;
  border: 1px dashed #e5e7eb;
}

.no-comments-icon {
  font-size: 40px;
  margin-bottom: 10px;
}

.comment-item {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 16px;
  display: flex;
  gap: 14px;
  transition: box-shadow 0.2s, border-color 0.2s;
}

.comment-item:hover {
  border-color: #fed7aa;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.04);
}

.comment-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 16px;
  font-weight: 600;
  flex-shrink: 0;
}

.comment-body {
  flex: 1;
  min-width: 0;
}

.comment-meta {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
  flex-wrap: wrap;
}

.comment-user {
  font-weight: 600;
  color: #1f2937;
  font-size: 14px;
}

.comment-stars {
  display: flex;
  gap: 1px;
}

.comment-stars .star {
  font-size: 14px;
  color: #e5e7eb;
}

.comment-stars .star.filled {
  color: #f59e0b;
}

.comment-time {
  font-size: 12px;
  color: #9ca3af;
  margin-left: auto;
}

.comment-content {
  font-size: 14px;
  color: #4b5563;
  line-height: 1.7;
  word-break: break-word;
}

.delete-comment-btn {
  background: transparent;
  color: #ef4444;
  border: 1px solid #fecaca;
  padding: 3px 10px;
  border-radius: 6px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
  margin-left: 4px;
}

.delete-comment-btn:hover {
  background: #fee2e2;
}

/* 结果预览面板 */
.preview-panel {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.pdf-preview-wrapper {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.pdf-embed {
  flex: 1;
  width: 100%;
  min-height: 500px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}

.no-preview {
  text-align: center;
  color: #94a3b8;
  padding: 60px;
  font-size: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.no-preview-icon {
  font-size: 48px;
  margin-bottom: 8px;
}

.no-preview-tip {
  font-size: 13px;
  color: #cbd5e1;
}
</style>
